/**
 * Resolves what the TSA OSD panel mirrors from the shared device state for the
 * currently selected target. The dock and gateway slots never come back as
 * `undefined`: an offline dock (no OSD yet, or removed by SET_DEVICE_OFFLINE)
 * must still render its panel with placeholder values instead of throwing on
 * `deviceInfo.dock.basic_osd`.
 */
export interface OsdMirrorTarget {
  visible: boolean
  is_dock: boolean
  sn: string
  gateway_sn: string
}

export interface OsdMirrorSource<Dock, Device, Gateway> {
  dockInfo: Record<string, Dock>
  deviceInfo: Record<string, Device>
  gatewayInfo: Record<string, Gateway>
}

export interface OsdMirrorDefaults<Dock, Device, Gateway> {
  dock: () => Dock
  device: () => Device
  gateway: () => Gateway
}

export interface OsdMirror<Dock, Device, Gateway> {
  dock: Dock
  /** Undefined while a dock target has no aircraft OSD; the dock panel guards this slot. */
  device: Device | undefined
  gateway: Gateway
}

type DockLike = { basic_osd?: { sub_device?: { device_sn?: string } } }

export function resolveSelectedOsdMirror<Dock extends DockLike, Device, Gateway> (
  source: OsdMirrorSource<Dock, Device, Gateway>,
  target: OsdMirrorTarget,
  defaults: OsdMirrorDefaults<Dock, Device, Gateway>
): OsdMirror<Dock, Device, Gateway> {
  if (!target.visible) {
    return { dock: defaults.dock(), device: defaults.device(), gateway: defaults.gateway() }
  }
  const gateway = (target.gateway_sn && source.gatewayInfo[target.gateway_sn]) || defaults.gateway()
  if (target.is_dock) {
    const dock = (target.gateway_sn && source.dockInfo[target.gateway_sn]) || defaults.dock()
    const droneSn = dock.basic_osd?.sub_device?.device_sn || target.sn
    return {
      dock,
      device: droneSn ? source.deviceInfo[droneSn] : undefined,
      gateway,
    }
  }
  return {
    dock: defaults.dock(),
    device: (target.sn && source.deviceInfo[target.sn]) || defaults.device(),
    gateway,
  }
}
